import { Area, CartesianGrid, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";

import type { Metric, SeriesPoint } from "../api";
import { extremeLabels, formatMetricValue, metricLabel } from "../format";

/**
 * The competitive set's evolution for one variant and metric: the min-to-max
 * band, the median, and the average.
 *
 * Meteoric drew this as a hand-rolled inline SVG with its own hover crosshair
 * and tick maths. That is exactly the second chart idiom the portal exists to
 * end (I-7), so it goes through `ChartFrame` and Recharts instead. The visual
 * grammar is preserved: a shaded band for the spread, a solid median, a dashed
 * average.
 */
export function BenchmarkChart({
  series,
  metric,
  marketplace,
}: {
  series: SeriesPoint[];
  metric: Metric;
  marketplace: string;
}) {
  const labels = extremeLabels(metric);

  // Recharts stacks an Area from a base value plus a height, so the band is
  // expressed as [min, span] rather than [min, max].
  const data = series.map((point) => ({
    obs_date: point.obs_date,
    median: point.median,
    avg: point.avg,
    min: point.min,
    span: point.min !== null && point.max !== null ? point.max - point.min : null,
    max: point.max,
    max_brand: point.max_brand,
    min_brand: point.min_brand,
    n: point.n,
  }));

  return (
    <ChartFrame
      title={`${metricLabel(metric)} evolution of the competitive set`}
      pointCount={data.length}
      height={300}
      emptyMessage="No observations for this variant and marketplace yet."
    >
      <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" />
        <XAxis dataKey="obs_date" tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" />
        <YAxis
          tick={{ fontSize: 12 }}
          stroke="var(--color-ks-grey-1)"
          width={72}
          tickFormatter={(value: number) => formatMetricValue(metric, value, marketplace)}
          // A BSR axis runs the wrong way round otherwise: rank 1 is the BEST
          // position, so the good end has to be at the top like every other chart.
          reversed={metric === "bsr"}
          domain={["auto", "auto"]}
        />
        <Tooltip
          contentStyle={{
            background: "var(--color-ks-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-ks-sm)",
            fontSize: 12,
          }}
          formatter={(value: unknown, key: unknown) => {
            const num = typeof value === "number" ? value : null;
            if (key === "span") return [formatMetricValue(metric, num, marketplace), `${labels.min} to ${labels.max} spread`];
            const name =
              key === "median" ? "Median" : key === "avg" ? "Average" : key === "min" ? labels.min : String(key);
            return [formatMetricValue(metric, num, marketplace), name];
          }}
          labelFormatter={(label: unknown, payload) => {
            // `n` belongs in the tooltip, not the legend: a day built from two
            // competitors and one built from twenty are not the same claim, and
            // the difference is only visible per-day.
            const n = payload?.[0]?.payload?.n;
            return typeof n === "number" ? `${label} · n = ${n} competitors` : String(label);
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {/* The band is drawn first so the two lines sit on top of it. */}
        <Area
          dataKey="min"
          stackId="band"
          stroke="none"
          fill="none"
          legendType="none"
          isAnimationActive={false}
          name={labels.min}
        />
        <Area
          dataKey="span"
          stackId="band"
          stroke="none"
          fill="var(--color-ks-purple-3)"
          fillOpacity={0.3}
          isAnimationActive={false}
          name={`${labels.min} to ${labels.max}`}
        />
        <Line
          type="monotone"
          dataKey="avg"
          stroke="var(--color-ks-purple-shade)"
          strokeWidth={1.5}
          strokeDasharray="5 4"
          dot={false}
          isAnimationActive={false}
          name="Average"
        />
        <Line
          type="monotone"
          dataKey="median"
          stroke="var(--color-ks-purple)"
          strokeWidth={2.25}
          dot={false}
          isAnimationActive={false}
          name="Median"
        />
      </ComposedChart>
    </ChartFrame>
  );
}
